import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';

interface RunningJobsState {
  hasActiveJobs: boolean;
  jobCount: number;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to check if a workspace has running or pending jobs
 * @param workspaceId - The ID of the workspace to check
 * @returns Object containing hasActiveJobs, jobCount, loading state, and error
 */
export const useWorkspaceRunningJobs = (workspaceId: string | undefined): RunningJobsState => {
  const authenticatedApi = useAuthenticatedApi();
  const [hasActiveJobs, setHasActiveJobs] = useState(false);
  const [jobCount, setJobCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRunningJobs = useCallback(async () => {
    if (!workspaceId) {
      setHasActiveJobs(false);
      setJobCount(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedApi.get(apiEndpoints.workspaceRunningJobs(workspaceId));
      setHasActiveJobs(response.has_active_jobs ?? false);
      setJobCount(response.job_count ?? 0);
    } catch (err) {
      console.error('Error fetching running jobs:', err);
      setError(err as Error);
      setHasActiveJobs(false);
      setJobCount(0);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, authenticatedApi]);

  useEffect(() => {
    fetchRunningJobs();
  }, [fetchRunningJobs]);

  return {
    hasActiveJobs,
    jobCount,
    loading,
    error,
    refetch: fetchRunningJobs,
  };
};

export default useWorkspaceRunningJobs;

